/**
 * OASIS Component - MVP Implementation
 * Main component for managing OASIS assessments
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FileText, Edit, CheckCircle, Eye
} from 'lucide-react';
import { GenericListView } from '../../common';
import OasisForm from './OasisForm';
import ReasonDialog from '../../common/ReasonDialog';
import { describeApiError } from '../../../utils/apiErrors';
import { useHasAnyPermission } from '../../../hooks/usePermission';
import { AssessmentsService, AssessmentStatus, AssessmentDiscipline, AssessmentDetail, AssessmentListItem } from '../../../services/patientmr/assessments.service';
import { 
  OasisListItem, 
  OasisAssessment,
  OasisType, 
  OasisFormData
} from '../../../types/patientmr/oasis';

interface OasisProps {
  expanded: boolean;
  toggleExpanded: () => void;
  patientId?: string;
  provider?: string;
  activeVisitId?: string;
}

const Oasis: React.FC<OasisProps> = ({ expanded, toggleExpanded, patientId, provider, activeVisitId }) => {
  // Pagination constants
  const ASSESSMENTS_PER_PAGE = 6;

  // Permission checks for OASIS assessments
  const canReadAssessment = useHasAnyPermission([
    'patientmr:phi:assessment:read' as any,
    'patientmr:phi:assessment:readall' as any
  ]);

  const canCreateAssessment = useHasAnyPermission([
    'patientmr:phi:assessment:create' as any,
    'patientmr:phi:assessment:createall' as any
  ]);

  const canUpdateAssessment = useHasAnyPermission([
    'patientmr:phi:assessment:update' as any,
    'patientmr:phi:assessment:updateall' as any
  ]);

  
  // State management
  const [activeTab, setActiveTab] = useState<'list' | 'form'>('list');
  const [assessments, setAssessments] = useState<OasisListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reopening, setReopening] = useState(false);
  // Assessment waiting for a reopen reason (Phase 4: reopen requires a reason)
  const [reopenTargetId, setReopenTargetId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  
  // Pagination state
  const [paginationToken, setPaginationToken] = useState<string | undefined>(undefined);
  const [hasMoreAssessments, setHasMoreAssessments] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Use the actual visitId for creating assessments
  const visitId = activeVisitId;
  
  // Form state
  const [editingAssessment, setEditingAssessment] = useState<OasisAssessment | null>(null);
  const [formData, setFormData] = useState<(OasisFormData & { assessmentId?: string }) | null>(null);
  const [, setSelectedAssessmentType] = useState<OasisType | null>(null);
  
  // Add ref to track form modifications and prevent unnecessary resets
  const formModificationRef = useRef(false);
  const preservedFormDataRef = useRef<(OasisFormData & { assessmentId?: string }) | null>(null);

  // Ref for deduplication
  const hasLoadedOasisRef = useRef<string | null>(null);

  // Load assessments when component mounts or patient changes
  useEffect(() => {
    if (!expanded || !patientId) {
      return;
    }

    // Skip if already loaded for this patient
    if (hasLoadedOasisRef.current === patientId || loading) {
      return;
    }

    hasLoadedOasisRef.current = patientId;
    loadAssessments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, patientId]); // loadAssessments and loading intentionally excluded to avoid circular dependency

  // Reset ref when collapsed
  useEffect(() => {
    if (!expanded) {
      hasLoadedOasisRef.current = null;
    }
  }, [expanded]);

  // Add click outside handler to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.dropdown-container')) {
        document.querySelectorAll('.dropdown-menu').forEach(dropdown => {
          dropdown.classList.add('hidden');
        });
      }
    };

    if (expanded) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [expanded]);

  // Load assessments from API using AssessmentsService
  const loadAssessments = async () => {
    if (!patientId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Get OASIS assessments by setting category=oasis query parameter with pagination
      const response = await AssessmentsService.getAssessments(patientId, { 
        category: 'oasis',
        limit: ASSESSMENTS_PER_PAGE.toString()
      });
      
      // Now we have OASIS assessments from the backend, no need to filter
      // The backend has already filtered by category=oasis
      const oasisAssessments = response.assessments.map(assessment => 
        mapAssessmentToOasisItem(assessment)
      );
      
      setAssessments(oasisAssessments);
      setPaginationToken(response.paginationToken);
      setHasMoreAssessments(response.hasMore);
      setHasLoadedOnce(true);
    } catch (err: any) {
      console.error('Error loading OASIS assessments:', err);
      setError('Failed to load OASIS assessments. Please try again.');
      // Reset ref on error to allow retry
      hasLoadedOasisRef.current = null;
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to map AssessmentListItem to OasisListItem
  const mapAssessmentToOasisItem = (assessment: AssessmentListItem): OasisListItem => {
    // Determine OASIS type based on assessmentType from API
    let oasisType = OasisType.OASIS_SOC; // Default
    
    // Map assessmentType string to OasisType enum
    if (assessment.assessmentType) {
      switch (assessment.assessmentType) {
        case 'OASIS_SOC':
          oasisType = OasisType.OASIS_SOC;
          break;
        case 'OASIS_ROC':
          oasisType = OasisType.OASIS_ROC;
          break;
        case 'OASIS_DISCHARGE':
          oasisType = OasisType.OASIS_DISCHARGE;
          break;
        default:
          oasisType = OasisType.OASIS_SOC;
      }
    }
    
    // No status mapping needed - use AssessmentStatus directly!
    
    return {
      assessmentId: assessment.assessmentId,
      patientId: patientId || '',
      type: oasisType,
      assessmentDate: assessment.date,
      status: assessment.status, // Use AssessmentStatus directly, no mapping
      hippsCode: '', // We'll get this from detailed assessment data when needed
      clinicianName: assessment.providerName,
      createdAt: assessment.date,
      updatedAt: assessment.date
    };
  };

  // Refresh assessments
  const handleRefresh = async () => {
    if (!patientId || refreshing) return;
    
    try {
      setRefreshing(true);
      setError(null);
      
      // Reset ref to allow reload
      hasLoadedOasisRef.current = null;
      
      // Reset pagination and get first page
      const response = await AssessmentsService.getAssessments(patientId, { 
        category: 'oasis',
        limit: ASSESSMENTS_PER_PAGE.toString()
      });
      
      // Now we have OASIS assessments from the backend, no need to filter
      // The backend has already filtered by category=oasis
      const oasisAssessments = response.assessments.map(assessment => 
        mapAssessmentToOasisItem(assessment)
      );
      
      setAssessments(oasisAssessments);
      setPaginationToken(response.paginationToken);
      setHasMoreAssessments(response.hasMore);
      
      // Set ref after successful refresh
      hasLoadedOasisRef.current = patientId;
    } catch (err: any) {
      console.error('Error refreshing OASIS assessments:', err);
      setError('Failed to refresh assessments. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  // Load more assessments
  const handleLoadMoreAssessments = async () => {
    if (!patientId || !paginationToken || loadingMore) return;
    
    try {
      setLoadingMore(true);
      setError(null);
      
      const response = await AssessmentsService.getAssessments(patientId, {
        category: 'oasis',
        limit: ASSESSMENTS_PER_PAGE.toString(),
        paginationToken: paginationToken
      });
      
      const moreOasisAssessments = response.assessments.map(assessment => 
        mapAssessmentToOasisItem(assessment)
      );
      
      setAssessments(prev => [...prev, ...moreOasisAssessments]);
      setPaginationToken(response.paginationToken);
      setHasMoreAssessments(response.hasMore);
    } catch (err: any) {
      console.error('Error loading more OASIS assessments:', err);
      setError('Failed to load more assessments. Please try again.');
    } finally {
      setLoadingMore(false);
    }
  };

  // Handle creating new assessment
  const handleNewAssessment = (type: OasisType) => {
    if (!patientId) {
      setError('Patient ID is required to create a new assessment.');
      return;
    }
    
    const newFormData = createEmptyOasisData(type, patientId);
    setFormData(newFormData);
    setSelectedAssessmentType(type);
    setEditingAssessment(null);
    setActiveTab('form');
  };

  // Handle editing existing assessment - PRESERVE current form state during loading
  const handleEditAssessment = async (assessmentId: string) => {
    if (!patientId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Use AssessmentsService to get assessment details
      const assessmentDetail = await AssessmentsService.getAssessmentDetails(patientId, assessmentId);
      
      // Get OASIS type from data fields
      const oasisType = ((assessmentDetail as any).assessmentType as OasisType) || 
                         (assessmentDetail.data?.oasisType as OasisType) || 
                         OasisType.OASIS_SOC;
      
      // Get assessment date from data.oasisData.M0030_startCareDate or current date
      const assessmentDate = assessmentDetail.data?.oasisData?.M0030_startCareDate || 
                             new Date().toISOString().split('T')[0];
      
      // Map provider name correctly based on the nested structure
      const provider = assessmentDetail.provider || {};
      const providerName = typeof provider === 'object' ? 
                          (provider as any)?.name || 'Unknown Provider' : 
                          String(provider) || 'Unknown Provider';
      
      // Map the status from AssessmentStatus to OasisStatus using our helper
      // Handle status from assessment detail - use directly, no mapping needed
      const oasisStatus = assessmentDetail.status;

      // Get timestamps from the detail object or fallback to current time
      const createdAt = (assessmentDetail as any).createdAt || new Date().toISOString();
      const updatedAt = (assessmentDetail as any).updatedAt || new Date().toISOString();
      
      // Convert to OasisAssessment format
      const oasisAssessment = {
        assessmentId: assessmentDetail.assessmentId || '',
        patientId: patientId,
        type: oasisType,
        assessmentDate: assessmentDate,
        status: oasisStatus,
        clinicianName: providerName,
        createdAt: createdAt,
        updatedAt: updatedAt,
        hippsCode: assessmentDetail.data?.hippsCode || '',
        // Any other required fields
        responses: assessmentDetail.data?.oasisData || {}
      } as unknown as OasisAssessment;
      
      // Allow editing in all states - Save button will be hidden based on status
      // The form can be viewed/edited, but changes can only be saved in DRAFT state
      
      setEditingAssessment(oasisAssessment);
      const formDataWithId = mapAssessmentToFormData(oasisAssessment) as (OasisFormData & { assessmentId: string });
      formDataWithId.assessmentId = assessmentId;
      setFormData(formDataWithId);
      setSelectedAssessmentType(oasisAssessment.type);
      setActiveTab('form');
    } catch (err: any) {
      console.error('Error loading assessment for editing:', err);
      setError('Failed to load assessment for editing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle saving assessment - PREVENT form data loss during save
  const handleSaveAssessment = async (data: OasisFormData & { assessmentId?: string, _finalizeAfterSave?: boolean, _saveAsDraft?: boolean }) => {
    if (!patientId) return;
    
    try {
      setSaving(true);
      setError(null);
      
      // Check if this is a finalize operation
      const shouldFinalize = (data as any)._finalizeAfterSave;
      
      // Remove the finalize flag from the data before saving
      if ((data as any)._finalizeAfterSave) {
        delete (data as any)._finalizeAfterSave;
      }
      if ((data as any)._saveAsDraft) {
        delete (data as any)._saveAsDraft;
      }
      
      // Map OasisFormData to AssessmentDetail for use with AssessmentsService
      const assessmentData: AssessmentDetail & { assessmentType: OasisType } = {
        provider: provider || 'Unknown Provider',
        discipline: AssessmentDiscipline.RN, // This could be different, depending on how OASIS fits into your assessment types
        assessmentType: data.type, // Include assessmentType at the top level of the payload
        data: {
          oasisType: data.type,
          oasisData: data.responses,
          // Include other OASIS-specific fields
          hippsCode: (data as any).hippsCode || ''
        }
      };
      
      let savedAssessment: AssessmentDetail;
      
      if (editingAssessment) {
        // Update existing assessment
        savedAssessment = await AssessmentsService.updateAssessment(
          patientId, 
          editingAssessment.assessmentId, 
          {
            ...assessmentData,
            assessmentId: editingAssessment.assessmentId
          }
        );
        
        // Convert the saved assessment back to OasisAssessment format
        const updatedOasisAssessment = {
          assessmentId: savedAssessment.assessmentId || editingAssessment.assessmentId,
          patientId: patientId,
          type: data.type,
          assessmentDate: new Date().toISOString(),
          status: savedAssessment.status, // Use AssessmentStatus directly
          clinicianName: savedAssessment.provider,
          createdAt: editingAssessment.createdAt,
          updatedAt: new Date().toISOString(),
          hippsCode: savedAssessment.data?.hippsCode || ''
        } as OasisAssessment;
        
        // Update local state
        setAssessments(prev => 
          prev.map(item => 
            item.assessmentId === editingAssessment.assessmentId
              ? {
                  ...item,
                  assessmentDate: updatedOasisAssessment.assessmentDate,
                  status: updatedOasisAssessment.status,
                  hippsCode: updatedOasisAssessment.hippsCode || '',
                  updatedAt: updatedOasisAssessment.updatedAt
                }
              : item
          )
        );
        
        // Update the current form data with the assessment ID if finalize was requested
        if (shouldFinalize) {
          setFormData(prev => prev ? { ...prev, assessmentId: editingAssessment.assessmentId } : null);
        }
      } else {
        // Create new assessment
        savedAssessment = await AssessmentsService.createAssessment(
          patientId,
          visitId || 'NO_VISIT',
          assessmentData
        );
        
        // Convert the created assessment to OasisAssessment format
        const newOasisAssessment = {
          assessmentId: savedAssessment.assessmentId || '',
          patientId: patientId,
          type: data.type,
          assessmentDate: new Date().toISOString(),
          status: savedAssessment.status, // Use AssessmentStatus directly
          hippsCode: savedAssessment.data?.hippsCode || '',
          clinicianName: savedAssessment.provider,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        } as OasisAssessment;
        
        // Add to local state
        const newListItem: OasisListItem = {
          assessmentId: newOasisAssessment.assessmentId,
          patientId: newOasisAssessment.patientId,
          type: newOasisAssessment.type,
          assessmentDate: newOasisAssessment.assessmentDate,
          status: newOasisAssessment.status,
          hippsCode: newOasisAssessment.hippsCode || '',
          clinicianName: newOasisAssessment.clinicianName,
          createdAt: newOasisAssessment.createdAt,
          updatedAt: newOasisAssessment.updatedAt
        };
        
        setAssessments(prev => [newListItem, ...prev]);
        
        // Update the current form data with the assessment ID if finalize was requested
        if (shouldFinalize && savedAssessment.assessmentId) {
          setFormData(prev => prev ? { ...prev, assessmentId: savedAssessment.assessmentId } : null);
        }
      }
      
      // If this was a finalize operation, now finalize the assessment
      if (shouldFinalize) {
        try {
          const assessmentIdToFinalize = editingAssessment ? editingAssessment.assessmentId : savedAssessment.assessmentId;
          if (assessmentIdToFinalize) {
            await AssessmentsService.finalizeAssessment(patientId, assessmentIdToFinalize);
            
            // Update the assessment status in local state
            setAssessments(prev => 
              prev.map(item => 
                item.assessmentId === assessmentIdToFinalize
                  ? { ...item, status: AssessmentStatus.SUBMITTED }
                  : item
              )
            );
          }
        } catch (finalizeError: any) {
          console.error('Error finalizing OASIS assessment:', finalizeError);
          setError('Assessment saved but failed to finalize. You can finalize it later from the assessments list.');
          // Don't return here - we still want to proceed to form cleanup
        }
      }
      
      // Return to list view
      handleCancelForm();
      
    } catch (err: any) {
      console.error('Error saving OASIS assessment:', err);
      setError('Failed to save assessment. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle submitting assessment
  const handleSubmitAssessment = async (assessmentId: string) => {
    if (!patientId) return;
    
    try {
      setSubmitting(true);
      setError(null);
      
      // Use AssessmentsService to submit the assessment
      await AssessmentsService.submitAssessment(patientId, assessmentId);
      
      // Update local state to show as submitted
      setAssessments(prev => 
        prev.map(item => 
          item.assessmentId === assessmentId
            ? { ...item, status: AssessmentStatus.SUBMITTED }
            : item
        )
      );
      
      // If this is the currently editing assessment, update its status
      if (editingAssessment && editingAssessment.assessmentId === assessmentId) {
        setEditingAssessment(prev => prev ? { ...prev, status: AssessmentStatus.SUBMITTED } : null);
      }
      
    } catch (err: any) {
      console.error('Error submitting OASIS assessment:', err);
      setError('Failed to submit assessment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle reopening assessment
  const handleReopenAssessment = async (assessmentId: string, reason: string) => {
    if (!patientId) return;
    
    try {
      setReopening(true);
      setError(null);
      
      // Use AssessmentsService to reopen the assessment
      await AssessmentsService.reopenAssessment(patientId, assessmentId, reason);
      setReopenTargetId(null);
      
      // Update local state to show as draft
      setAssessments(prev => 
        prev.map(item => 
          item.assessmentId === assessmentId
            ? { ...item, status: AssessmentStatus.DRAFT }
            : item
        )
      );
      
      // If this is the currently editing assessment, update its status
      if (editingAssessment && editingAssessment.assessmentId === assessmentId) {
        setEditingAssessment(prev => prev ? { ...prev, status: AssessmentStatus.DRAFT } : null);
      }
      
    } catch (err: any) {
      console.error('Error reopening OASIS assessment');
      setReopenTargetId(null);
      setError(describeApiError(err, 'Failed to reopen assessment. Please try again.'));
    } finally {
      setReopening(false);
    }
  };

  // Handle approving/finalizing assessment
  const handleApproveOasis = async (assessmentId: string) => {
    if (!patientId) return;
    
    try {
      setSubmitting(true);
      setError(null);
      
      // Use AssessmentsService to finalize the assessment
      await AssessmentsService.finalizeAssessment(patientId, assessmentId);
      
      // Update local state to show as complete
      setAssessments(prev => 
        prev.map(item => 
          item.assessmentId === assessmentId
            ? { ...item, status: AssessmentStatus.COMPLETE }
            : item
        )
      );
      
      // If this is the currently editing assessment, update its status and close form
      if (editingAssessment && editingAssessment.assessmentId === assessmentId) {
        setEditingAssessment(null);
        setFormData(null);
        setActiveTab('list');
      }
      
    } catch (err: any) {
      console.error('Error approving OASIS assessment:', err);
      
      // Handle specific error cases
      if (err.response?.status === 404) {
        setError('Assessment not found. It may have been deleted.');
        await loadAssessments();
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        setError('You do not have permission to approve this assessment.');
      } else {
        setError('Failed to approve assessment. Please try again.');
      }
      
      // Reload assessments on error to ensure consistency
      await loadAssessments();
    } finally {
      setSubmitting(false);
    }
  };

  // Handle canceling form - PRESERVE data for future use
  const handleCancelForm = () => {
    // Preserve current form data before clearing
    if (formData) {
      preservedFormDataRef.current = formData;
      formModificationRef.current = false;
    }
    
    setFormData(null);
    setEditingAssessment(null);
    setSelectedAssessmentType(null);
    setActiveTab('list');
    setSaving(false);
  };

  // Get status color for badge
  const getStatusColor = (status: AssessmentStatus): string => {
    switch (status) {
      case AssessmentStatus.DRAFT:
        return 'bg-gray-50 text-gray-700 border border-gray-200';
      case AssessmentStatus.SUBMITTED:
        return 'bg-blue-50 text-blue-700 border border-blue-200';
      case AssessmentStatus.QA:
        return 'bg-yellow-50 text-yellow-700 border border-yellow-200';
      case AssessmentStatus.COMPLETE:
        return 'bg-green-50 text-green-700 border border-green-200';
      default:
        return 'bg-gray-50 text-gray-700 border border-gray-200';
    }
  };

  // Get type color for badge
  const getTypeColor = (type: OasisType): string => {
    switch (type) {
      case OasisType.OASIS_SOC:
        return 'bg-blue-50 text-blue-700 border border-blue-200';
      case OasisType.OASIS_ROC:
        return 'bg-purple-50 text-purple-700 border border-purple-200';
      case OasisType.OASIS_DISCHARGE:
        return 'bg-gray-50 text-gray-700 border border-gray-200';
      default:
        return 'bg-gray-50 text-gray-700 border border-gray-200';
    }
  };
  
  // Helper function to get display name for OASIS type
  const getOasisTypeDisplayName = (type: OasisType): string => {
    switch (type) {
      case OasisType.OASIS_SOC:
        return 'Start of Care';
      case OasisType.OASIS_ROC:
        return 'Recertification';
      case OasisType.OASIS_DISCHARGE:
        return 'End of Care';
      default:
        return 'Unknown Type';
    }
  };
  
  // Helper function to get display name for OASIS status
  const getOasisStatusDisplayName = (status: AssessmentStatus): string => {
    switch (status) {
      case AssessmentStatus.DRAFT:
        return 'Draft';
      case AssessmentStatus.SUBMITTED:
        return 'Submitted';
      case AssessmentStatus.QA:
        return 'QA';
      case AssessmentStatus.COMPLETE:
        return 'Complete';
      case AssessmentStatus.CURRENT:
        return 'Current';
      case AssessmentStatus.EXPIRED:
        return 'Expired';
      default:
        return 'Unknown Status';
    }
  };
  
  // Helper function to create empty OASIS data - MEMOIZED to prevent resets
  const createEmptyOasisData = useCallback((type: OasisType, patientId: string): OasisFormData & { assessmentId?: string } => {
    // Only create new data if we don't have preserved data
    if (preservedFormDataRef.current && preservedFormDataRef.current.type === type) {
      return preservedFormDataRef.current;
    }
    
    // Create a base structure that includes required fields for OasisFormData
    const baseData = {
      type,
      patientId,
      assessmentDate: new Date().toISOString().split('T')[0],
      // Include any other required fields based on actual OasisFormData structure
      responses: {}, // This is required according to the error message
      // Additional fields as needed
      data: {
        patientInfo: {},
        clinicalRecord: {},
        functionalStatus: {},
        medications: {}
      }
    };
    
    // Cast to OasisFormData type and store in ref for preservation
    const newFormData = baseData as unknown as (OasisFormData & { assessmentId?: string });
    preservedFormDataRef.current = newFormData;
    return newFormData;
  }, []);
  
  // Helper function to map assessment to form data
  const mapAssessmentToFormData = (assessment: OasisAssessment): OasisFormData => {
    // Extract start care date from responses if available
    const startCareDate = assessment.responses?.M0030_startCareDate || assessment.assessmentDate;
    
    // For discharge date, use appropriate field or undefined
    // Adjust field name based on your schema - M0906_dischargeDate or another field name
    const dischargeDate = assessment.type === OasisType.OASIS_DISCHARGE ? 
                          (assessment.responses as any)?.M0906_dischargeDate || undefined : 
                          undefined;
    
    return {
      type: assessment.type,
      patientId: assessment.patientId,
      assessmentDate: startCareDate,
      episodeStartDate: assessment.type === OasisType.OASIS_SOC ? startCareDate : undefined,
      episodeEndDate: dischargeDate,
      responses: assessment.responses || {}
      // Note: hippsCode is not directly included in OasisFormData type but is stored in the backend
    };
  };

  // OASIS type options for dropdown
  const oasisTypeOptions = canCreateAssessment ? [
    {
      label: 'Start of Care (SOC)',
      description: 'Initial assessment for new patients',
      color: 'bg-blue-500',
      onClick: () => handleNewAssessment(OasisType.OASIS_SOC),
    },
    {
      label: 'Recertification (ROC)',
      description: '60-day recertification assessment',
      color: 'bg-purple-500',
      onClick: () => handleNewAssessment(OasisType.OASIS_ROC),
    },
    {
      label: 'End of Care',
      description: 'Final assessment at episode end',
      color: 'bg-gray-500',
      onClick: () => handleNewAssessment(OasisType.OASIS_DISCHARGE),
    },
  ] : [];

  // Sort assessments by date
  const sortedAssessments = [...assessments].sort(
    (a, b) => new Date(b.assessmentDate).getTime() - new Date(a.assessmentDate).getTime()
  );

  return (
    <GenericListView
      // ListView props
      title="OASIS Assessments"
      icon={<FileText size={20} />}
      expanded={expanded}
      onToggleExpand={toggleExpanded}
      // Data props
      items={sortedAssessments}
      keyExtractor={(assessment) => assessment.assessmentId}
      columns={[
        {
          key: 'date',
          label: 'Date',
          width: 'w-28',
          render: (assessment) => (
            <span className="text-sm font-medium text-gray-900">
              {new Date(assessment.assessmentDate).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
          ),
        },
        {
          key: 'clinician',
          label: 'Clinician',
          width: 'flex-1 min-w-0',
          render: (assessment) => (
            <span className="text-sm text-gray-700 truncate pr-3">{assessment.clinicianName}</span>
          ),
        },
        {
          key: 'type',
          label: 'Type',
          width: 'w-32',
          align: 'center',
          render: (assessment) => (
            <span className={`px-3 py-1 rounded text-xs font-semibold ${getTypeColor(assessment.type)}`}>
              {getOasisTypeDisplayName(assessment.type)}
            </span>
          ),
        },
        {
          key: 'hippsCode',
          label: 'HIPPS Code',
          width: 'w-24',
          align: 'center',
          render: (assessment) => (
            <span className="text-sm text-gray-700 font-mono">{assessment.hippsCode || 'N/A'}</span>
          ),
        },
        {
          key: 'status',
          label: 'Status',
          width: 'w-28',
          align: 'center',
          render: (assessment) => (
            <span className={`px-3 py-1 rounded text-xs font-semibold ${getStatusColor(assessment.status)}`}>
              {getOasisStatusDisplayName(assessment.status)}
            </span>
          ),
        },
      ]}
      actions={[
        {
          icon: <Eye size={14} />,
          label: 'View assessment',
          onClick: (assessment) => handleEditAssessment(assessment.assessmentId),
          className: 'p-1.5 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors',
        },
        {
          icon: <Edit size={14} />,
          label: 'Edit assessment',
          onClick: (assessment) => handleEditAssessment(assessment.assessmentId),
          isVisible: (assessment) => canUpdateAssessment && assessment.status === AssessmentStatus.DRAFT,
          className: 'p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors',
        },
        {
          icon: <CheckCircle size={14} />,
          label: 'Submit assessment',
          onClick: (assessment) => handleSubmitAssessment(assessment.assessmentId),
          isVisible: (assessment) => canUpdateAssessment && assessment.status === AssessmentStatus.DRAFT,
          className: 'p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors',
        },
      ]}
      // Mobile card config
      mobileCard={{
        primaryText: (assessment) => assessment.clinicianName,
        secondaryText: (assessment) =>
          new Date(assessment.assessmentDate).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }),
        tertiaryText: (assessment) => (assessment.hippsCode ? `HIPPS: ${assessment.hippsCode}` : undefined),
        badges: (assessment) => [
          {
            label: getOasisTypeDisplayName(assessment.type),
            className: getTypeColor(assessment.type),
          },
          {
            label: getOasisStatusDisplayName(assessment.status),
            className: getStatusColor(assessment.status),
          },
        ],
        actions: (assessment) => [
          {
            icon: <Eye size={18} />,
            label: 'View assessment',
            onClick: () => handleEditAssessment(assessment.assessmentId),
          },
          ...(canUpdateAssessment && assessment.status === AssessmentStatus.DRAFT
            ? [
                {
                  icon: <Edit size={18} />,
                  label: 'Edit assessment',
                  onClick: () => handleEditAssessment(assessment.assessmentId),
                },
                {
                  icon: <CheckCircle size={18} />,
                  label: 'Submit assessment',
                  onClick: () => handleSubmitAssessment(assessment.assessmentId),
                  className:
                    'p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors',
                },
              ]
            : []),
        ],
      }}
      // Action bar props
      onRefresh={handleRefresh}
      refreshing={refreshing}
      loading={loading}
      newButtonLabel={canCreateAssessment ? "New OASIS Assessment" : undefined}
      newButtonOptions={canCreateAssessment ? oasisTypeOptions : undefined}
      // Pagination props
      hasMore={hasMoreAssessments}
      onLoadMore={handleLoadMoreAssessments}
      loadingMore={loadingMore}
      // State props
      error={error}
      onDismissError={() => setError(null)}
      hasLoadedOnce={hasLoadedOnce}
      // Empty state props
      emptyStateIcon={<FileText size={48} className="text-gray-300" />}
      emptyStateTitle="No OASIS assessments available"
      emptyStateMessage="Start by creating a new OASIS assessment using the button above."
      // Show list only when in 'list' tab
      showList={activeTab === 'list'}
    >
      {/* Form View */}
      {activeTab === 'form' && formData && (
        <OasisForm
          formData={formData}
          patientId={patientId || ''}
          onSave={handleSaveAssessment}
          onSubmit={editingAssessment ? () => handleSubmitAssessment(editingAssessment.assessmentId) : undefined}
          onReopen={editingAssessment ? () => setReopenTargetId(editingAssessment.assessmentId) : undefined}
          onApprove={editingAssessment ? () => handleApproveOasis(editingAssessment.assessmentId) : undefined}
          onCancel={handleCancelForm}
          saving={saving}
          submitting={submitting}
          reopening={reopening}
          isEditing={!!editingAssessment}
          oasisStatus={editingAssessment?.status}
          canReadAssessment={canReadAssessment}
          canCreateAssessment={canCreateAssessment}
          canUpdateAssessment={canUpdateAssessment}
        />
      )}
      <ReasonDialog
        open={reopenTargetId !== null}
        title="Reopen assessment"
        description="The assessment returns to draft and a new version is started. The reason is kept in its history."
        label="Reason for reopening"
        confirmLabel="Reopen"
        busy={reopening}
        onConfirm={(reason) => (reopenTargetId ? handleReopenAssessment(reopenTargetId, reason) : undefined)}
        onCancel={() => setReopenTargetId(null)}
      />
    </GenericListView>
  );
};

export default Oasis;
